import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { ToastService } from '@libs/src/toast/toast.service';

import {
  GetGroupSuccess,
  GROUPS_ADD_ROLE_TYPE,
  GROUPS_DELETE_ROLE_TYPE,
  GROUPS_GET_TYPE,
  GROUPS_UPDATE_ROLE_TYPE,
  GROUPS_UPDATE_TYPE,
  GroupsAction,
  UpdateGroupError,
  UpdateGroupRoleSuccess,
  UpdateGroupSuccess,
} from './groups.actions';
import { Group, GroupRole } from '@libs/src/models/group.model';

const GroupApiUrl = (groupId: string) => `/api/v2/groups/${groupId}`;

@Injectable()
export class GroupsEffects {
  getGroup: Observable<GroupsAction> = createEffect(() =>
    this.actions.pipe(
      ofType(GROUPS_GET_TYPE),
      concatMap((action: GroupsAction) =>
        this.http
          .get(GroupApiUrl(action.payload.group._id))
          .pipe(catchError(() => EMPTY)),
      ),
      map((response: Group) => GetGroupSuccess({ group: response })),
    ),
  );

  updateGroup: Observable<GroupsAction> = createEffect(() =>
    this.actions.pipe(
      ofType(GROUPS_UPDATE_TYPE),
      concatMap((action: GroupsAction) => {
        return this.http
          .patch(GroupApiUrl(action.payload.group._id), action.payload.group)
          .pipe(
            map((response: Group) => {
              if (!action.payload.isSilent) {
                this.toastService.showSuccess('Update successful!');
              }
              return UpdateGroupSuccess({ group: response });
            }),
            catchError((responseError: HttpErrorResponse) => {
              if (!action.payload.isSilent) {
                this.toastService.showError(
                  responseError.error.message ||
                    'Something went wrong with your update.',
                );
              }
              const originalGroup = action.payload.originalGroup;
              return of(UpdateGroupError({ group: originalGroup || {} }));
            }),
          );
      }),
    ),
  );

  addGroupRole: Observable<GroupsAction> = createEffect(() =>
    this.actions.pipe(
      ofType(GROUPS_ADD_ROLE_TYPE),
      concatMap((action: GroupsAction) => {
        const payload = action.payload;
        return this.http
          .post(`${GroupApiUrl(payload.group._id)}/roles`, payload.role)
          .pipe(
            map((response: GroupRole[]) => ({
              group: payload.group,
              roles: response,
            })),
            catchError((error: HttpErrorResponse) => {
              this.toastService.showError(error.message);
              const originalGroup = action.payload.originalGroup;
              return originalGroup
                ? of({
                    group: payload.group,
                    roles: originalGroup.roles,
                  })
                : EMPTY;
            }),
          );
      }),
      map(({ group, roles }) => UpdateGroupRoleSuccess({ group, roles })),
    ),
  );

  deleteGroupRole: Observable<GroupsAction> = createEffect(() =>
    this.actions.pipe(
      ofType(GROUPS_DELETE_ROLE_TYPE),
      concatMap((action: GroupsAction) => {
        const payload = action.payload;
        return this.http
          .delete(`${GroupApiUrl(payload.group._id)}/roles/${payload.role._id}`)
          .pipe(
            map((response: GroupRole[]) => ({
              group: payload.group,
              roles: response,
            })),
            catchError((error: HttpErrorResponse) => {
              this.toastService.showError(error.message);
              const originalGroup = action.payload.originalGroup;
              return originalGroup
                ? of({
                    group: payload.group,
                    roles: originalGroup.roles,
                  })
                : EMPTY;
            }),
          );
      }),
      map(({ group, roles }) => UpdateGroupRoleSuccess({ group, roles })),
    ),
  );

  updateGroupRole: Observable<GroupsAction> = createEffect(() =>
    this.actions.pipe(
      ofType(GROUPS_UPDATE_ROLE_TYPE),
      concatMap((action: GroupsAction) => {
        const payload = action.payload;
        return this.http
          .put(
            `${GroupApiUrl(payload.group._id)}/roles/${payload.role._id}`,
            payload.role,
          )
          .pipe(
            map((response: GroupRole[]) => ({
              group: payload.group,
              roles: response,
            })),
            catchError((error: HttpErrorResponse) => {
              this.toastService.showError(error.message);
              const originalGroup = action.payload.originalGroup;
              return originalGroup
                ? of({
                    group: payload.group,
                    roles: originalGroup.roles,
                  })
                : EMPTY;
            }),
          );
      }),
      map(({ group, roles }) => UpdateGroupRoleSuccess({ group, roles })),
    ),
  );

  constructor(
    private actions: Actions,
    private http: HttpClient,
    private toastService: ToastService,
  ) {}
}
